// IMPORTANT for ISO dates:
// Our backend reports start_of_week of 0 to be Sunday. Sunday is 7 according to ISO 8601

import type { WeekNumbers } from 'luxon';
import { DateTime, Interval } from 'luxon';
import type { StartOfWeek } from 'types/account';
import { UserTimeFormat } from 'types/user';

export function formatDateWithISOFallback(date: DateTime, format?: string) {
  return format ? date.toFormat(format) : date.toISODate();
}

export function getFirstDayOfWeekCore(date: DateTime, weekStartsOn: StartOfWeek) {
  if (weekStartsOn === 0) {
    // Week starts Sunday
    if (date.weekday === 7) {
      // Already on Sunday, return it
      return date;
    } else {
      // Go to Monday of this week, then back 1 day to get Sunday
      return date.startOf('week').minus({ days: 1 });
    }
  } else if (weekStartsOn === 1) {
    // Week starts Monday - use Luxon default
    return date.startOf('week');
  } else if (weekStartsOn === 6) {
    // Week starts Saturday
    if (date.weekday === 6) {
      // Already on Saturday, return it
      return date;
    } else if (date.weekday === 7) {
      // Sunday - go back 1 day to get Saturday
      return date.minus({ days: 1 });
    } else {
      // Monday-Friday (weekday 1-5) - go back to previous Saturday
      // weekday 1 (Monday) needs to go back 2 days
      // weekday 2 (Tuesday) needs to go back 3 days, etc.
      return date.minus({ days: date.weekday + 1 });
    }
  }

  return date.startOf('week');
}

export function getFirstDayOfWeek(date: string, weekStartsOn: StartOfWeek, format?: string) {
  const startDay = getFirstDayOfWeekCore(DateTime.fromISO(date), weekStartsOn);
  return formatDateWithISOFallback(startDay, format);
}

export function getWeekNumber(date: string, weekStartsOn: StartOfWeek) {
  const startDay: DateTime = DateTime.fromISO(date);
  let weekNumber = startDay.weekNumber;
  if (weekStartsOn === 0) {
    // if we're on Sunday, it's the first day of the week
    if (startDay.weekday === 7) {
      weekNumber = (weekNumber + 1) as WeekNumbers;
    }
  } else if (weekStartsOn === 6) {
    // Week starts Saturday
    if (startDay.weekday === 6 || startDay.weekday === 7) {
      // Saturday or Sunday should be part of the next week number
      weekNumber = (weekNumber + 1) as WeekNumbers;
    }
  }
  return weekNumber.toLocaleString(undefined, { minimumIntegerDigits: 2 });
}

getFirstDayOfWeek.core = getFirstDayOfWeekCore;

function getLastDayOfWeekCore(date: DateTime, weekStartsOn: StartOfWeek) {
  if (weekStartsOn === 0) {
    // Week starts Sunday, ends Saturday
    // if we're on Sunday, add a day so it puts us into "next" week
    if (date.weekday === 7) {
      date = date.plus({ days: 1 }).endOf('week').minus({ days: 1 });
      // if it's Saturday, we're already at the end of the week
    } else if (date.weekday !== 6) {
      // otherwise, get the end of the week and subtract a day
      date = date.endOf('week').minus({ days: 1 });
    }
  } else if (weekStartsOn === 1) {
    // Week starts Monday, ends Sunday - use Luxon's default
    return date.endOf('week');
  } else if (weekStartsOn === 6) {
    // Week starts Saturday, ends Friday
    // if we're on Saturday, add a day so it puts us into "next" week
    if (date.weekday === 6) {
      date = date.plus({ days: 1 }).endOf('week').minus({ days: 2 });
      // if it's Friday, we're already at the end of the week
    } else if (date.weekday !== 5) {
      // otherwise, get the end of the week and subtract 2 days
      date = date.endOf('week').minus({ days: 2 });
    }
  }

  return date;
}
export function getLastDayOfWeek(date: string, weekStartsOn: StartOfWeek, format?: string) {
  const endDay = getLastDayOfWeekCore(DateTime.fromISO(date), weekStartsOn);
  return formatDateWithISOFallback(endDay, format);
}

getLastDayOfWeek.core = getLastDayOfWeekCore;

export function getFirstDayOfMonth(date: string, format?: string) {
  const firstDay = DateTime.fromISO(date).startOf('month');
  return formatDateWithISOFallback(firstDay, format);
}

export function getLastDayOfMonth(date: string, format?: string) {
  const lastDay = DateTime.fromISO(date).endOf('month');
  return formatDateWithISOFallback(lastDay, format);
}

export function getFirstDayOfLastWeek(date: string, weekStartsOn: StartOfWeek, format?: string) {
  const startDay = DateTime.fromISO(getFirstDayOfWeek(date, weekStartsOn)).minus({ weeks: 1 });
  return formatDateWithISOFallback(startDay, format);
}

export function getFirstDayOfNextWeek(date: string, weekStartsOn: StartOfWeek, format?: string) {
  const startDay = DateTime.fromISO(getFirstDayOfWeek(date, weekStartsOn)).plus({
    weeks: 1,
  });
  return formatDateWithISOFallback(startDay, format);
}

export function isAfter(date: string, compareDate: string) {
  return DateTime.fromISO(date) > DateTime.fromISO(compareDate);
}
export function isAfterDay(date: string, compareDate: string) {
  return DateTime.fromISO(date).toISODate() > DateTime.fromISO(compareDate).toISODate();
}
export function isBefore(date: string, compareDate: string) {
  return DateTime.fromISO(date) < DateTime.fromISO(compareDate);
}
export function isBeforeDay(date: string, compareDate: string) {
  return DateTime.fromISO(date).toISODate() < DateTime.fromISO(compareDate).toISODate();
}

export function isSameDay(date: string, compareDate: string) {
  return DateTime.fromISO(date).hasSame(DateTime.fromISO(compareDate), 'day');
}

export function isSameYear(date: string, compareDate: string) {
  return DateTime.fromISO(date).hasSame(DateTime.fromISO(compareDate), 'year');
}

export function isSameWeek(date: string, compareDate: string, weekStartsOn: StartOfWeek) {
  const compareDateWeekStart = getFirstDayOfWeek(compareDate, weekStartsOn);
  const compareDateWeekEnd = getLastDayOfWeek(compareDate, weekStartsOn);

  // standard hasSame check provided by Luxon will not suffice here as
  // it doesn't support customising the first day of the week
  return isBetweenDates(date, compareDateWeekStart, compareDateWeekEnd);
}

export function isSameMonth(date: string, compareDate: string) {
  return DateTime.fromISO(date).hasSame(DateTime.fromISO(compareDate), 'month');
}

export function isBetweenDates(date: string, start: string, end: string) {
  return Interval.fromDateTimes(
    DateTime.fromISO(start),
    // Luxon doesn't include end date by default, MomentJS implementation of this did
    DateTime.fromISO(end).plus({ days: 1 })
  ).contains(DateTime.fromISO(date));
}

export function getDaysInWeek(date: string, weekStartsOn: StartOfWeek) {
  const startDay = getFirstDayOfWeek(date, weekStartsOn);
  return getDaysInRange(startDay, DateTime.fromISO(startDay).plus({ days: 6 }).toISODate());
}

export function getDaysInRange(fromDate: string, toDate: string, format?: string) {
  const rangeInterval = Interval.fromDateTimes(
    DateTime.fromISO(fromDate),
    DateTime.fromISO(toDate)
  );

  const days: Array<string> = [];
  for (const day of generateDays(rangeInterval)) {
    const date = formatDateWithISOFallback(day, format);
    days.push(date);
  }
  return days;
}

export function getDayRangeInWeek(date: string, weekStartsOn: StartOfWeek, format?: string) {
  const range = getDaysInWeek(date, weekStartsOn);
  return {
    from: format ? DateTime.fromISO(range[0]).toFormat(format) : range[0],
    to: format
      ? DateTime.fromISO(range[range.length - 1]).toFormat(format)
      : range[range.length - 1],
  };
}

interface GetDaysInMonthOptions {
  includeOutsideDays?: boolean;
  weekStartsOn: StartOfWeek;
  format?: string;
}
export function getDaysInMonth(
  date: string,
  // includeOutsideDays is needed for calendar-like display
  options: GetDaysInMonthOptions = {
    includeOutsideDays: false,
    weekStartsOn: 0,
  }
) {
  const { includeOutsideDays, weekStartsOn, format } = options;

  const startDay = includeOutsideDays
    ? getFirstDayOfWeek(getFirstDayOfMonth(date), weekStartsOn)
    : getFirstDayOfMonth(date);

  const endDay = includeOutsideDays
    ? getLastDayOfWeek(getLastDayOfMonth(date), weekStartsOn)
    : getLastDayOfMonth(date);

  return getDaysInRange(startDay, endDay, format);
}

export function getDayRangeInMonth(date: string, options?: GetDaysInMonthOptions) {
  const range = getDaysInMonth(date as string, options);
  return {
    from: range[0],
    to: range[range.length - 1],
  };
}

export function getDaysInSpan(
  date: string,
  span: 'day' | 'week' | 'month',
  weekStartsOn: StartOfWeek,
  includeOutsideDays: boolean = false
) {
  switch (span) {
    case 'week':
      return getDaysInWeek(date, weekStartsOn);
    case 'month':
      return getDaysInMonth(date, {
        includeOutsideDays: includeOutsideDays,
        weekStartsOn: weekStartsOn,
      });
    default:
      return [DateTime.fromISO(date).toISODate()];
  }
}

export function* generateDays(interval: Interval) {
  let date = interval.start.startOf('day');
  while (date <= interval.end) {
    yield date;
    date = date.plus({ days: 1 });
  }
}

export function* generateMonths(interval: Interval) {
  let date = interval.start.startOf('month');
  while (date <= interval.end) {
    yield date;
    date = date.plus({ months: 1 });
  }
}

export function* generateYears(interval: Interval) {
  let date = interval.start.startOf('year');
  while (date <= interval.end) {
    yield date;
    date = date.plus({ years: 1 });
  }
}

export function getMonthsInYear(date: Date, format?: string) {
  const day = DateTime.fromJSDate(date);
  const startDay = day.startOf('year');
  const endDay = day.endOf('year');
  const range = Interval.fromDateTimes(startDay, endDay);
  const months: Array<string> = [];

  for (const day of generateMonths(range)) {
    months.push(formatDateWithISOFallback(day, format));
  }
  return months;
}

export function getDayRangeInYear(date: string, format?: string) {
  return {
    from: formatDateWithISOFallback(DateTime.fromISO(date).startOf('year'), format),
    to: formatDateWithISOFallback(DateTime.fromISO(date).endOf('year'), format),
  };
}

export function getDaysInYear(date: string, format?: string) {
  const { from, to } = getDayRangeInYear(date as string, format);

  return getDaysInRange(from, to, format);
}

export function getYearRange(date: string, format?: string, yearsToShow: number = 20) {
  const halfCount = Math.floor(yearsToShow / 2);
  const startYear = DateTime.fromISO(date).startOf('year').minus({ years: halfCount });
  const endYear = startYear.plus({ years: yearsToShow });
  const range = Interval.fromDateTimes(startYear, endYear);

  const years = [];

  for (const day of generateYears(range)) {
    years.push(formatDateWithISOFallback(day, format));
  }
  return years;
}

export function getCurrentMomentOnDay(date: string) {
  const origin = DateTime.fromISO(date);
  const day = origin.day;
  const month = origin.month;
  const year = origin.year;
  const { hour, minute, second } = DateTime.now();

  return DateTime.now().set({ year, month, day, hour, minute, second }).toISO();
}

export function getTimeSpanWithSmartYear(
  dates: {
    from?: string | null | undefined;
    to?: string | null | undefined;
  } = { from: null, to: null },
  formats: {
    noYear: string;
    withYear: string;
  } = { noYear: 'LLL d', withYear: 'LLL d, yyyy' }
) {
  const { from, to } = dates;

  if (!from || !to) {
    return null;
  }

  const fromMoment = DateTime.fromISO(from);
  const toMoment = DateTime.fromISO(to);
  const yearDiffers = !fromMoment.hasSame(toMoment, 'year');

  if (yearDiffers) {
    return `${fromMoment.toFormat(formats.withYear)} — ${toMoment.toFormat(formats.withYear)}`;
  }
  return `${fromMoment.toFormat(formats.noYear)} — ${toMoment.toFormat(formats.withYear)}`;
}

export const getHoursAndMinutesFromSeconds = (seconds: number) => {
  const totalMinutes = Math.floor(seconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  let formatted = '';
  formatted += hours > 0 ? `${hours}h` : '';
  formatted += minutes > 0 ? ` ${minutes}m` : '';

  if (hours === 0 && minutes === 0) {
    return '0m';
  }

  return formatted;
};

export const getHoursOrMinutesFromSeconds = (seconds: number) => {
  const totalMinutes = Math.floor(seconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  if (hours > 0) {
    return `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  if (seconds > 0) {
    return `${seconds}s`;
  }
  return '0h';
};

export const getLastWeeksRange = (
  today: string,
  lastWeeks: number,
  startOfWeek: StartOfWeek
): { since: string; until: string } => {
  const lastDayOfWeek = getLastDayOfWeek(today, startOfWeek);
  const firstDayOfWeek = getFirstDayOfWeek(today, startOfWeek);
  const startOfRange = DateTime.fromISO(firstDayOfWeek).minus({
    weeks: lastWeeks - 1,
  });

  return {
    since: startOfRange.toISODate(),
    until: DateTime.fromISO(lastDayOfWeek).toISODate(),
  };
};

export const getWeeksFromRange = (
  since: string,
  until: string
): Array<{ since: string; until: string }> => {
  const theEnd = DateTime.fromISO(until);
  let sinceTemp = DateTime.fromISO(since);
  let untilTemp = sinceTemp.plus({ days: 6 });
  const temp: Array<{ since: string; until: string }> = [];

  while (isBefore(sinceTemp.toISODate(), theEnd.toISODate())) {
    temp.push({
      since: sinceTemp.toISODate(),
      until: untilTemp.toISODate(),
    });
    sinceTemp = sinceTemp.plus({ days: 7 });
    untilTemp = untilTemp.plus({ days: 7 });
  }
  return temp;
};

export interface MoveDateOptions {
  direction: 'back' | 'forward';
  timespan: 'day' | 'week' | 'month' | 'year';
  startOfWeek: StartOfWeek;
}
export function moveDateBackOrForward(
  dateRange: { from: string; to: string },
  { direction = 'back', timespan = 'day', startOfWeek = 1 }: MoveDateOptions
) {
  const operation = direction === 'back' ? 'minus' : 'plus';
  const dtFrom = DateTime.fromISO(dateRange.from);
  const dtTo = DateTime.fromISO(dateRange.to);
  const difference = Math.abs(dtFrom.diff(dtTo, timespan).days) + 1;
  // Adjust the dateRange by adding/subtracting a timespan
  const from = dtFrom[operation]({ [timespan]: difference });
  const to = dtTo[operation]({ [timespan]: difference });

  if (timespan === 'week') {
    return getDayRangeInWeek(from.toISODate(), startOfWeek);
  }

  return { from: from.startOf(timespan).toISODate(), to: to.endOf(timespan).toISODate() };
}

export function getNextInstanceOfISODay(dayNumberAsString: string) {
  const requestedDay = DateTime.fromFormat(dayNumberAsString, 'c');
  const today = DateTime.now().startOf('day');

  if (requestedDay < today) {
    return requestedDay.plus({ weeks: 1 }).toISODate();
  }
  return requestedDay.toISODate();
}

export function formatTime(time: string, timeFormat: UserTimeFormat) {
  return DateTime.fromISO(time)
    .toLocaleString(
      timeFormat === UserTimeFormat.H12
        ? { ...DateTime.TIME_SIMPLE, hourCycle: 'h12', hour12: true }
        : DateTime.TIME_24_SIMPLE
    )
    .toLocaleLowerCase();
}

export function getUserFriendlyDateTime(
  date: string,
  hourFormat: UserTimeFormat,
  dateFormat: string = 'MMM d'
) {
  const now = DateTime.local();
  const dateTime = DateTime.fromISO(date);
  const isToday = dateTime.hasSame(now, 'day');
  const isYesterday = dateTime.hasSame(now.minus({ days: 1 }), 'day');
  const timeFormat = hourFormat === UserTimeFormat.H12 ? 'h:mm a' : 'HH:mm';

  if (isToday) return `Today ${dateTime.toFormat(timeFormat)}`;
  if (isYesterday) return `Yesterday ${dateTime.toFormat(timeFormat)}`;

  return dateTime.toFormat(`${dateFormat} ${timeFormat}`);
}

export function naivelyGetOrdinalSuffix(day: number) {
  let num;

  if (day <= 0) {
    num = 4;
  } else if ((day > 3 && day < 21) || day % 10 > 3) {
    num = 0;
  } else {
    num = day % 10;
  }

  return day + ['th', 'st', 'nd', 'rd', ''][num];
}

export function getNativeDateFromString(date: string) {
  return DateTime.fromISO(date).toJSDate();
}

export function getNumberOfDaysInRange(dateA: DateTime, dateB: DateTime, includeEndDate = true) {
  return Math.abs(dateA.diff(dateB, 'days').days) + (includeEndDate ? 1 : 0);
}

export function createRangeFromYear(rangeLength: number, yearStartFrom: string) {
  const years: Array<string> = [];

  for (let step = 0; step < rangeLength; step++) {
    const year = DateTime.fromISO(yearStartFrom).plus({ year: step }).toISODate();
    years.push(year);
  }
  return years;
}

export function calculateOverlappingSeconds(
  first: { from: string; to: string },
  second: { from: string; to: string }
): number {
  const intervalA = Interval.fromISO(`${first.from}/${first.to}`);
  const intervalB = Interval.fromISO(`${second.from}/${second.to}`);
  return intervalA.intersection(intervalB)?.length('seconds') || 0;
}

export interface DSTTransitionDetails {
  isDSTTransition: boolean;
  transitionHour: number;
  offsetChangeHours: number;
}

//Checks whether a specific day is a DST transition day
export function isDSTTransition(dateTime: DateTime) {
  const nextDay = dateTime.plus({ days: 1 });
  const isDSTTransition = dateTime.offset !== nextDay.offset;
  return isDSTTransition;
}

/**
 * Returns necessary details to adjust pixel based timestamps for DST transition
 * @param dateTime - The date and time to check for DST transition
 * @returns The DST transition details
 */
export function getDSTTransitionDetails(dateTime: DateTime): DSTTransitionDetails {
  let currentHour = dateTime.startOf('day');
  if (isDSTTransition(dateTime)) {
    // If DST transition, find the hour at which the transition occurs
    for (let h = 0; h < 24; h++) {
      const nextHour = currentHour.plus({ hours: 1 });
      if (nextHour.offset !== currentHour.offset) {
        return {
          isDSTTransition: true,
          transitionHour: currentHour.hour + 1,
          offsetChangeHours: Math.floor((nextHour.offset - currentHour.offset) / 60),
        };
      }
      currentHour = nextHour;
    }
  }
  // Default values if not a DST transition
  return {
    isDSTTransition: false,
    transitionHour: 0,
    offsetChangeHours: 0,
  };
}
